import { Provider } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { TransactionEvents } from '@app/domain/events';
import { CurrencyExchangeDomainService } from '@app/domain/services';
import {
  GetCurrencyExchangesUseCase,
  GetCurrencyExchangeByIdUseCase,
  CountCurrencyExchangesUseCase,
  FindSimilarExchangeUseCase,
  CreateCurrencyExchangeUseCase,
  UpdateCurrencyExchangeUseCase,
  DeleteCurrencyExchangeUseCase,
} from '@app/domain/use-cases';
import type { ICurrencyExchangeRepository } from '@app/domain/ports';
import { CURRENCY_EXCHANGE_REPOSITORY } from '../../tokens/infrastructure.tokens';

/**
 * Provides Currency Exchange domain services and use cases.
 *
 * Includes:
 * - CurrencyExchangeDomainService: Business rules and validation
 * - 7 Use Cases: Get, GetById, Count, FindSimilar, Create, Update, Delete
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function provideCurrencyExchangeDomain(): Provider[] {
  return [
    // Domain Service
    {
      provide: CurrencyExchangeDomainService,
      useFactory: () => new CurrencyExchangeDomainService(),
      deps: [],
    },

    // Use Cases - Query
    {
      provide: GetCurrencyExchangesUseCase,
      useFactory: (repository: ICurrencyExchangeRepository) => new GetCurrencyExchangesUseCase(repository),
      deps: [CURRENCY_EXCHANGE_REPOSITORY],
    },
    {
      provide: GetCurrencyExchangeByIdUseCase,
      useFactory: (repository: ICurrencyExchangeRepository) => new GetCurrencyExchangeByIdUseCase(repository),
      deps: [CURRENCY_EXCHANGE_REPOSITORY],
    },
    {
      provide: CountCurrencyExchangesUseCase,
      useFactory: (repository: ICurrencyExchangeRepository) => new CountCurrencyExchangesUseCase(repository),
      deps: [CURRENCY_EXCHANGE_REPOSITORY],
    },
    {
      provide: FindSimilarExchangeUseCase,
      useFactory: (repository: ICurrencyExchangeRepository) => new FindSimilarExchangeUseCase(repository),
      deps: [CURRENCY_EXCHANGE_REPOSITORY],
    },

    // Use Cases - Commands
    {
      provide: CreateCurrencyExchangeUseCase,
      useFactory: (repository: ICurrencyExchangeRepository, domainService: CurrencyExchangeDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new CreateCurrencyExchangeUseCase(repository, domainService, dispatch);
      },
      deps: [CURRENCY_EXCHANGE_REPOSITORY, CurrencyExchangeDomainService],
    },
    {
      provide: UpdateCurrencyExchangeUseCase,
      useFactory: (repository: ICurrencyExchangeRepository, domainService: CurrencyExchangeDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new UpdateCurrencyExchangeUseCase(repository, domainService, dispatch);
      },
      deps: [CURRENCY_EXCHANGE_REPOSITORY, CurrencyExchangeDomainService],
    },
    {
      provide: DeleteCurrencyExchangeUseCase,
      useFactory: (repository: ICurrencyExchangeRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new DeleteCurrencyExchangeUseCase(repository, dispatch);
      },
      deps: [CURRENCY_EXCHANGE_REPOSITORY],
    },
  ];
}
